<script>
  import Header from "../components/Header.svelte";
  import Footer from "../components/Footer.svelte";
  import { onMount } from "svelte";
  import LandingHeader from "../components/LandingHeader.svelte";

  // Scroll to the top when the component is mounted
  onMount(() => {
    window.scrollTo(0, 0);
  });
</script>

<LandingHeader />
<br /><br />

<body>
  <section class="security-policy">
    <h1>Security Policy</h1>
    <p class="last-updated">Last Updated: November 1, 2024</p>

    <p>
      This Security Policy outlines the security measures and protocols adopted
      by TruLens, a digital media verification solution by Jionex IT Services
      Co. L.L.C (“Jionex,” “we,” “us,” or “our”), to protect user data, maintain
      data integrity, and ensure operational security. Our commitment is to
      uphold the highest standards of data protection and compliance with UAE
      laws and industry best practices.
    </p>

    <h2>Introduction</h2>
    <p>
      TruLens provides tools for authenticating digital media by verifying the
      source, authenticity, and context of content. We prioritize the security
      of all media and data processed through our services to combat tampering,
      fraud, and misinformation. Our approach encompasses infrastructure
      security, organizational security, product security, and data privacy.
    </p>

    <h2>Infrastructure Security</h2>

    <h3>Authentication and Access Control</h3>
    <ul>
      <li>
        <strong>Unique Authentication Requirements:</strong> TruLens enforces unique
        user identification and authentication for all production databases, requiring
        individual usernames and secure passwords or Secure Socket Shell (SSH) keys.
      </li>
      <li>
        <strong>Multi-Factor Authentication (MFA):</strong> Authorized personnel
        must use multi-factor authentication for remote access to production systems.
      </li>
      <li>
        <strong>Production Environment Access:</strong> Access to the production
        environment is limited to authorized users, with privileged access to databases,
        applications, and networks restricted based on business needs.
      </li>
      <li>
        <strong>Encryption Key Management:</strong> Access to encryption keys is
        restricted to authorized personnel only, with strict role-based control and
        logging of access events.
      </li>
    </ul>

    <h3>Network Security</h3>
    <ul>
      <li>
        <strong>Firewall Management:</strong> Firewalls are configured and regularly
        reviewed to control inbound and outbound traffic, prevent unauthorized access,
        and segment networks based on functionality.
      </li>
      <li>
        <strong>Intrusion Detection and Monitoring:</strong> Continuous network monitoring
        is in place to detect potential intrusions, supported by an intrusion detection
        system (IDS) that logs and analyzes unusual activity.
      </li>
      <li>
        <strong>Data Encryption:</strong> All sensitive customer data is encrypted
        both at rest and in transit using industry-standard encryption protocols.
      </li>
    </ul>

    <h3>Access Control Policies</h3>
    <ul>
      <li>
        <strong>Access Review and Revocation:</strong> Access rights are reviewed
        at least semi-annually, and access is promptly revoked upon termination or
        role changes.
      </li>
      <li>
        <strong>Secure Remote Access:</strong> Remote access to production systems
        is only permitted through encrypted connections to ensure data integrity
        and confidentiality.
      </li>
      <li>
        <strong>Log Management:</strong> All access and activity logs are systematically
        managed and monitored to identify any unauthorized actions.
      </li>
    </ul>

    <h2>Organizational Security</h2>

    <h3>Asset Management and Disposal</h3>
    <ul>
      <li>
        <strong>Asset Inventory:</strong> An inventory of all production systems
        is maintained, tracking hardware and software assets.
      </li>
      <li>
        <strong>Secure Disposal:</strong> Electronic media containing confidential
        data are securely destroyed or purged following best practices, with certificates
        of destruction issued when necessary.
      </li>
    </ul>

    <h3>Employee Security Policies</h3>
    <ul>
      <li>
        <strong>Background Checks:</strong> Background checks are conducted on new
        employees to ensure the integrity of personnel handling sensitive data.
      </li>
      <li>
        <strong>Confidentiality and Code of Conduct:</strong> All employees and contractors
        sign confidentiality agreements, and adherence to our Code of Conduct is
        mandatory. Violations may result in disciplinary action.
      </li>
      <li>
        <strong>Security Awareness Training:</strong> Employees undergo mandatory
        security training upon hiring and annually thereafter to stay informed on
        security policies and potential threats.
      </li>
    </ul>

    <h3>Visitor and Physical Security</h3>
    <ul>
      <li>
        <strong>Visitor Procedures:</strong> Access to secure areas requires sign-in,
        badges, and an authorized escort. Access logs are reviewed periodically.
      </li>
      <li>
        <strong>Mobile Device Management (MDM):</strong> TruLens employs MDM to manage
        and secure mobile devices, ensuring compliance with organizational security
        policies.
      </li>
    </ul>

    <h2>Product Security</h2>

    <h3>Data Protection and Encryption</h3>
    <ul>
      <li>
        <strong>Encryption Standards:</strong> Data at rest and in transit is encrypted
        using industry-standard protocols, with particular attention to safeguarding
        media files and metadata in TruLens.
      </li>
      <li>
        <strong>Data Transmission:</strong> Confidential and sensitive data are transmitted
        using secure protocols to prevent unauthorized access over public networks.
      </li>
      <li>
        <strong>Control Self-Assessments and Penetration Testing:</strong> TruLens
        performs annual security assessments, including penetration tests. Identified
        vulnerabilities are addressed promptly per service level agreements (SLAs).
      </li>
    </ul>

    <h3>Vulnerability Management</h3>
    <ul>
      <li>
        <strong>System Monitoring and Maintenance:</strong> A comprehensive system
        monitoring tool tracks infrastructure and application performance, with alerts
        configured for pre-set thresholds.
      </li>
      <li>
        <strong>Regular Security Patching:</strong> Operating systems, applications,
        and firmware are routinely updated with the latest security patches to mitigate
        vulnerabilities.
      </li>
    </ul>

    <h2>Internal Security Procedures</h2>

    <h3>Incident Response</h3>
    <ul>
      <li>
        <strong>Incident Response Plan:</strong> TruLens has a documented incident
        response plan, tested annually, to ensure efficient handling of security
        and privacy incidents. All incidents are tracked, resolved, and communicated
        to affected parties as necessary.
      </li>
      <li>
        <strong>Logging and Reporting:</strong> Security incidents are logged and
        analyzed to determine root causes and prevent future occurrences.
      </li>
    </ul>

    <h3>Business Continuity and Disaster Recovery (BCDR)</h3>
    <ul>
      <li>
        <strong>BCDR Plans:</strong> Comprehensive business continuity and disaster
        recovery plans are in place to maintain service continuity. These plans are
        reviewed and tested annually to ensure readiness in the event of an outage
        or security incident.
      </li>
      <li>
        <strong>Data Backup:</strong> Data backups are performed regularly and stored
        securely. Backup integrity is verified through regular testing.
      </li>
    </ul>

    <h3>Change and Configuration Management</h3>
    <ul>
      <li>
        <strong>Change Management Procedures:</strong> All software and infrastructure
        changes undergo formal review, testing, and approval before deployment to
        the production environment.
      </li>
      <li>
        <strong>Configuration Management:</strong> Configuration standards are documented
        and enforced across all systems to ensure consistency and compliance with
        security policies.
      </li>
      <li>
        <strong>Whistleblower Policy:</strong> TruLens has a whistleblower policy
        that provides an anonymous communication channel for employees and contractors
        to report security or compliance concerns.
      </li>
    </ul>

    <h2>Risk Management</h2>

    <h3>Risk Assessment</h3>
    <p>
      Annual risk assessments are conducted to identify and mitigate potential
      threats to data and operational security. These assessments cover
      environmental, technological, and regulatory risks and include strategies
      for fraud prevention.
    </p>

    <h3>Vendor Management</h3>
    <p>
      Third-party vendors undergo a thorough assessment, including privacy and
      security reviews, before engagement. All vendors must comply with
      TruLens’s security standards.
    </p>

    <h2>Data Privacy and Retention</h2>

    <h3>Data Classification and Retention</h3>
    <p>
      Data classification policies ensure sensitive information is appropriately
      protected and restricted. Data retention procedures define the secure
      disposal of customer and company data following UAE regulatory standards.
    </p>

    <h3>Customer Data Deletion</h3>
    <p>
      Upon customer termination, all confidential data is purged from the system
      in line with established policies to ensure customer data integrity and
      privacy.
    </p>

    <h2>Compliance and Governance</h2>

    <h3>Security Policy Reviews</h3>
    <p>
      TruLens’s security policies and procedures are documented, reviewed, and
      updated annually to reflect regulatory changes and evolving industry
      standards.
    </p>

    <h3>Board Oversight and Reporting</h3>
    <p>
      The TruLens management team provides annual cybersecurity and risk
      assessments to the board for oversight and guidance.
    </p>

    <h2>Contact Information</h2>
    <p>
      If you have questions or concerns regarding this Security Policy, please
      contact us at:
    </p>
    <p>
      <strong>Jionex IT Services Co. L.L.C</strong><br />
      Head Office: M5, Liberty Building, 18th Service Road, Al Garhoud, Dubai, U.A.E<br
      />
      Email: admin@trulens.ae || contact@jionex.com
    </p>
  </section>
</body>

<br /><br />

<Footer />

<style>
  * {
    box-sizing: border-box;
  }

  body {
    margin: 0;
    padding: 0;
    font-family: Arial, sans-serif;
    color: #333;
  }

  .security-policy {
    max-width: 95%;
    margin: auto;
    padding-top: 0 !important;
    padding: 20px;
    line-height: 1.6;
  }

  h1 {
    font-size: 3em;
    color: #003366;
    margin-bottom: 10px;
    text-align: start;
  }

  .last-updated {
    font-size: 1em;
    color: #666;
    text-align: start;
    margin-bottom: 20px;
  }

  h2 {
    font-size: 1.5em;
    color: #003366;
    margin-top: 20px;
    margin-bottom: 10px;
  }

  h3 {
    font-size: 1.25em;
    color: #004080;
    margin-top: 15px;
    margin-bottom: 5px;
  }

  ul {
    padding-left: 20px;
    margin-top: 10px;
  }

  ul li {
    margin-bottom: 8px;
  }

  p {
    font-size: 1em;
    margin-top: 8px;
    margin-bottom: 8px;
  }

  /* Media Queries */
  @media (max-width: 768px) {
    h1 {
      font-size: 1.8em;
    }

    h2 {
      font-size: 1.4em;
    }

    h3 {
      font-size: 1.2em;
    }
  }

  @media (max-width: 480px) {
    h1 {
      font-size: 1.6em;
    }

    h2 {
      font-size: 1.2em;
    }

    h3 {
      font-size: 1em;
    }

    .security-policy {
      padding: 10px;
    }
  }

  @media (max-width: 390px) {
    h1 {
      font-size: 1.5em;
    }

    h2 {
      font-size: 1.1em;
    }

    h3 {
      font-size: 0.9em;
    }

    .security-policy {
      padding: 8px;
    }
  }
</style>
